import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

export default function DeleteClientModal({
  isOpen,
  onClose,
  client,
  onConfirmDelete,
  isDeleting = false,
}) {
  const [confirmationText, setConfirmationText] = useState("");
  const [isValid, setIsValid] = useState(false);

  const requiredText = client ? `${client.firstName} ${client.lastName}` : "";

  useEffect(() => {
    setIsValid(confirmationText === requiredText);
  }, [confirmationText, requiredText]);

  useEffect(() => {
    if (isOpen) {
      setConfirmationText("");
      setIsValid(false);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid && !isDeleting) {
      console.log(
        `[Delete Client Modal] Confirming archival for client: ${client?.firstName} ${client?.lastName}`
      );
      onConfirmDelete();
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      console.log(`[Delete Client Modal] Modal closed without archival`);
      onClose();
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-900">
                  Delete Client?
                </h3>
                <p className="text-sm text-red-700">
                  Client will be archived for HIPAA compliance (7-year retention)
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isDeleting}
              className="p-1 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <XMarkIcon className="h-5 w-5 text-red-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Client Information */}
          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-4">
              You are about to archive the client record for:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                  {client.firstName?.[0]?.toUpperCase()}
                  {client.lastName?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {client.firstName} {client.lastName}
                  </p>
                  {client.email && (
                    <p className="text-sm text-gray-600">{client.email}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Danger Zone - What Gets Deleted */}
          <div className="mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">
                    Archive Client - HIPAA Compliant Soft Delete
                  </h4>
                  <p className="text-sm text-red-700 mb-3">
                    This action will <strong>archive</strong> the client record 
                    while maintaining data for HIPAA compliance (7-year retention).
                  </p>

                  <h5 className="text-sm font-semibold text-red-800 mb-2">
                    Will be ARCHIVED (data preserved):
                  </h5>
                  <ul className="text-sm text-red-700 space-y-1 ml-4">
                    <li>• All personal and medical information (retained)</li>
                    <li>• Client profile and demographics (retained)</li>
                    <li>• Family relationships and connections (retained)</li>
                    <li>• Portal access (immediately disabled)</li>
                  </ul>

                  <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                    <p className="text-xs font-semibold text-red-800">
                      ⚠️ CLIENT STATUS WILL BE SET TO "SOFT DELETED"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Zone - What Remains But May Not Function */}
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">
                    System Behavior After Archival
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    The archived client will be hidden from normal views but 
                    all associated data remains intact for compliance.
                  </p>

                  <h5 className="text-sm font-semibold text-amber-800 mb-2">
                    Will REMAIN ACCESSIBLE:
                  </h5>
                  <ul className="text-sm text-amber-700 space-y-1 ml-4">
                    <li>• All historical appointments and SOAP notes</li>
                    <li>• All invoices and payment records</li>
                    <li>• All uploaded files and documents</li>
                    <li>• Client communications and messaging history</li>
                    <li>• Full audit trail of all interactions</li>
                  </ul>

                  <div className="mt-3 p-3 bg-amber-100 border border-amber-300 rounded">
                    <p className="text-xs font-semibold text-amber-800">
                      ℹ️ Client will be hidden from client lists and searches
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To confirm archival, type the client's full name exactly:
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Required:{" "}
              <span className="font-mono font-semibold bg-gray-100 px-2 py-1 rounded">
                {requiredText}
              </span>
            </p>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type the client's full name here..."
              disabled={isDeleting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              autoComplete="off"
            />
            {confirmationText && !isValid && (
              <p className="text-sm text-red-600 mt-1">
                Name does not match. Please type exactly: {requiredText}
              </p>
            )}
            {isValid && (
              <p className="text-sm text-green-600 mt-1">✓ Name confirmed</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2">
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Archiving...</span>
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4" />
                  <span>Archive Client</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
